import cx from "classnames";
import React, { useMemo } from "react";
import type { ActiveAnalysis } from "../../../api/useActiveAnalysesStream";
import type { Project } from "../../../api/useProjects";
import { useSetups } from "../../../api/useSetups";
import type { Source } from "../../../api/useSources";
import { useCanUserDoOnProject } from "../../canUserDo";
import t from "../../../i18n";
import type { NonEmptyArray } from "diagnostics-lib/array";
import ChartCard from "@ui/Highcharts/ui/Card";
import Selector from "@ui/Selector";
import ActionMenu from "../Analysis/AnalysisActionMenu";
import PeriodSelector from "../Analysis/PeriodSelector";
import ToggleGroupBreaks from "../Analysis/ToggleGroupBreaks";
import useSelectedAnalyses from "../Analysis/useSelectedAnalyses";
import InfoTooltip from "../Chart/InfoTooltip";
import { commonLegendactiveSetup } from "../Chart/Legend/CommonLegend";
import type { SelectedQuantity } from "../useSelectedQuantities";
import type { SymptomChartType } from "./SymptomChartType";
import TogglePublicationDisplay, {
  usePublicationFlagDisplay,
} from "../Analysis/TogglePublicationDisplay";
import { ChartMenu } from "../Chart/Menu";
import { useBreaksBetweenGroups } from "../Analysis/useBreaksBetweenGroups";
import { FullViewportButtonMarker, FullViewportTrigger } from "@ui/FullViewportButton";
import { useSettings } from "../../../api/useSettings";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import ToggleDisplayInvalidPoints, {
  useDisplayInvalidPoints,
} from "../Analysis/ToggleDisplayInvalidPoints";
import {
  ExportWidgetButton,
  exportWidgetTarget,
  HIDE_ON_WIDGET_EXPORT,
} from "@ui/ExportWidgetButton";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import Text from "@ui/Text";
import { generateExportFileName } from "diagnostics-lib/string";

import useActiveAnalysesWithDisplayFilters from "../../../api/useActiveAnalysesWithDisplayFilters";
import SymptomHighCharts from "./SymptomHighCharts";
import { useProjectPeriod } from "../useProjectPeriod";
import { useHasQuantityDataInPeriod } from "../useNoDataIntervals";
import PeriodZoomAllButton from "../Chart/PeriodZoomAllButton";
import EvolutionSubHeader from "../EvolutionSubHeader";
import { DisplayFiltersBlankState } from "../EvolutionSubHeader/DisplayFiltersBlankState";

interface P {
  project: Project;
  source: Source;
  activeAnalyses: ActiveAnalysis[];
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  selectedChartOption: SymptomChartType;
  setSelectedChartOption: (option: SymptomChartType) => void;
}

const SymptomEvolution = ({
  project,
  source,
  activeAnalyses,
  selectedQuantities: selectedQuantitiesState,
  selectedChartOption,
  setSelectedChartOption,
}: P) => {
  const projectId = project.id;
  const setups = useSetups(project.id);

  const settings = useSettings();

  const { displayFilterRemovesAllActiveAnalyses } = useActiveAnalysesWithDisplayFilters(project.id);

  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", projectId);
  const canUserReadProject = useCanUserDoOnProject("PROJECT_READ", project.id);

  const [breakBetweenGroups] = useBreaksBetweenGroups({
    scope: projectId,
  });
  const [publicationFlagDisplayed] = usePublicationFlagDisplay({
    scope: projectId,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: projectId,
  });
  const chartTypeOptions = React.useMemo(
    () => [
      {
        value: "amplitude" as const,
        label: t("symptom.chart.header.option.amplitude"),
        description: t("symptom.chart.header.option.amplitude.description"),
      },
      {
        value: "gap" as const,
        label: t("symptom.chart.header.option.gap"),
        description: t("symptom.chart.header.option.gap.description"),
      },
      {
        value: "normalizedGap" as const,
        label: t("symptom.chart.header.option.normalizedGap"),
        description: t("symptom.chart.header.option.normalizedGap.description"),
      },
    ],
    [],
  );
  const selectedQuantities: NonEmptyArray<SelectedQuantity> = React.useMemo(
    () =>
      selectedChartOption === "normalizedGap"
        ? selectedQuantitiesState
        : [selectedQuantitiesState[0]],
    [selectedChartOption, selectedQuantitiesState],
  );

  const [period, setPeriod, extremes] = useProjectPeriod({
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });

  const hasDataInPeriod = useHasQuantityDataInPeriod(
    activeAnalyses,
    selectedQuantities,
    selectedChartOption,
    projectId,
    period,
  );

  const hasDataInAllPeriod = useHasQuantityDataInPeriod(
    activeAnalyses,
    selectedQuantities,
    selectedChartOption,
    projectId,
    extremes,
  );

  const handleZoomToAllPeriod = React.useCallback(
    () => setPeriod({ from: extremes.from, to: extremes.to }),
    [extremes.from, extremes.to, setPeriod],
  );

  const { selectedAnalyses } = useSelectedAnalyses(projectId, activeAnalyses);
  const analysis = selectedAnalyses[0];
  const analysisSetup = analysis ? getAnalysisSetup(setups, analysis) : null;

  const subtitle = React.useMemo(
    () => (
      <EvolutionSubHeader
        projectIdToFilter={projectId}
        allAnalysesMode={false}
        chartOptions={
          <InfoTooltip
            title={t("symptom.chart.header.option.title")}
            options={chartTypeOptions}
            link="/symptoms-page"
          >
            <div className={HIDE_ON_WIDGET_EXPORT}>
              <Selector
                options={chartTypeOptions}
                value={selectedChartOption}
                onChange={setSelectedChartOption}
              />
            </div>
          </InfoTooltip>
        }
        periodSettings={
          <>
            <PeriodZoomAllButton
              visible={
                !displayFilterRemovesAllActiveAnalyses && !hasDataInPeriod && hasDataInAllPeriod
              }
              onClick={handleZoomToAllPeriod}
              label={t("chart.period.zoom_all")}
            />
            <PeriodSelector
              source={source}
              project={project}
              analyses={activeAnalyses}
              boundaries="active-analyses"
            />
          </>
        }
        visibilitySettings={
          <ChartMenu
            marker={!breakBetweenGroups || publicationFlagDisplayed || displayInvalidPoints}
            tooltipOverlay={t("chart.display_options.tooltip")}
          >
            <ToggleDisplayInvalidPoints projectId={projectId} />
            <TogglePublicationDisplay projectId={project.id} />
            <ToggleGroupBreaks projectId={projectId} groupables={activeAnalyses} />
          </ChartMenu>
        }
        actionMenu={
          !displayFilterRemovesAllActiveAnalyses && (
            <ActionMenu
              event={null}
              analyses={activeAnalyses}
              project={project}
              canWriteProject={canUserWriteProject}
              canUserReadProject={canUserReadProject}
              allAnalysesMode={false}
              className={HIDE_ON_WIDGET_EXPORT}
            />
          )
        }
      />
    ),
    [
      activeAnalyses,
      breakBetweenGroups,
      canUserReadProject,
      canUserWriteProject,
      chartTypeOptions,
      displayFilterRemovesAllActiveAnalyses,
      displayInvalidPoints,
      handleZoomToAllPeriod,
      hasDataInAllPeriod,
      hasDataInPeriod,
      project,
      projectId,
      publicationFlagDisplayed,
      selectedChartOption,
      setSelectedChartOption,
      source,
    ],
  );

  const legendLeft = React.useMemo(
    () =>
      analysisSetup ? (
        <div
          className={cx(commonLegendactiveSetup, HIDE_ON_WIDGET_EXPORT)}
          title={t("project.measure.legend_right.active_setup.name", analysisSetup.name)}
        >
          {t("project.measure.legend_right.active_setup.name", analysisSetup.name)}
        </div>
      ) : null,
    [analysisSetup],
  );

  const title = useMemo(
    () => (
      <>
        <Text as="span" type="h4">
          {t("symptoms.chart.title")}
        </Text>
        <div style={{ marginLeft: "auto" }}>
          <ExportWidgetButton
            fileName={generateExportFileName({
              filename: t("symptoms.chart.title"),
              projectName: project.name,
              extension: "png",
            })}
          />
          <FullViewportTrigger style={{ marginLeft: spacings.xs }} />
        </div>
      </>
    ),
    [project.name],
  );

  if (displayFilterRemovesAllActiveAnalyses) {
    return (
      <ChartCard
        title={title}
        subtitle={subtitle}
        legendLeft={legendLeft}
        className={cx(symptomChartCard, FullViewportButtonMarker, exportWidgetTarget)}
      >
        <DisplayFiltersBlankState projectId={projectId} />
      </ChartCard>
    );
  }

  return (
    <SymptomHighCharts
      activeAnalyses={activeAnalyses}
      project={project}
      selectedChartOption={selectedChartOption}
      selectedQuantities={selectedQuantities}
      setups={setups}
      source={source}
      subtitle={subtitle}
      title={title}
      legendLeft={legendLeft}
    />
  );
};

export default React.memo(SymptomEvolution);

//#region CSS
const symptomChartCard = css`
  min-height: 600px;
  row-gap: ${spacings.m};
`;

//#endregion CSS
